import { MONTHS_IN_YEAR, LOWEST_INTEREST_REFI_TERM } from '../../../../constants/refinance'
import { Format } from '../../..'
import { useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { Text, Heading } from '@homebotapp/hb-react-component-catalog'
import { sampleLoan } from '../../../../constants/homePreview'
import { LockableInlineCurrencyText } from '../../CopyHelpers'
import { selectFirstCEL, selectInterestDiff } from '../../../../store/selectors/home/loans'
import { selectCurrentCELs } from '../../../../store/selectors/home/dictionary'
import { selectLowestRefiRate } from '../../../../store/selectors/mortgageRate'

export const MSG = defineMessages({
  multi: {
    id: 'Homeowner.interestCard.loanDescriptor.multiple',
    defaultMessage: '{qty} loans'
  },
  single: {
    id: 'Homeowner.interestCard.loanDescriptor',
    defaultMessage: '{term} year loan'
  },
  head: {
    id: 'Homeowner.interestCard.totalInterestHeadline',
    defaultMessage: 'Interest Adds Up'
  },
  goodHead: {
    id: 'Homeowner.interestCard.totalInterestHeadline.good',
    defaultMessage: 'Your interest amount is low. Good work.'
  },
  status: {
    id: 'Homeowner.interest.status',
    defaultMessage: 'There are ways to get ahead and save some of that interest.'
  },
  armStatus: {
    id: 'Homeowner.interest.armStatus',
    defaultMessage:
      'If you have a non-fixed rate mortgage, like an ARM, remember to pay attention to your loan terms as your interest rate could change over time.'
  },
  goodStatus: {
    id: 'Homeowner.interest.status.good',
    defaultMessage: 'There are ways to save more of that interest.'
  },
  saving: {
    id: 'Homeowner.interest.statusExplainer.withPayments',
    defaultMessage:
      "Over your {loanDescription} you were due to pay {interestAmount} in interest but since you're paying extra, you're on track to save {saveAmount} of that interest. {status}"
  },
  explainer: {
    id: 'Homeowner.interest.statusExplainer',
    defaultMessage: "Over your {loanDescription} you'll pay {amount} in interest. {status}"
  }
})

export interface PrincipalAndInterestExplanationProps {
  withCurrentHomeData: boolean
}

export const PrincipalAndInterestExplanation = ({ withCurrentHomeData }: PrincipalAndInterestExplanationProps) => {
  const intl = useIntl()
  let loan = useSelector(selectFirstCEL)
  const rate = useSelector(selectLowestRefiRate)
  const dictionary = useSelector(selectCurrentCELs)
  let loansLength = dictionary?.loans?.()
  let interestDiff = useSelector(selectInterestDiff)
  const periods = LOWEST_INTEREST_REFI_TERM * MONTHS_IN_YEAR
  let interestTotal = dictionary?.interestForFullLoanTotal?.()
  let interestStatus = dictionary?.interestStatus?.({ rate, periods })

  if (!withCurrentHomeData) {
    interestStatus = ''
    loansLength = 1
    // @ts-ignore
    loan = sampleLoan
    interestDiff = sampleLoan.facts.interestDiff
    interestTotal = sampleLoan.facts.totalInterest
  }

  const loanDescription =
    loansLength > 1
      ? intl.formatMessage(MSG.multi, {
          qty: Format.number(loansLength)
        })
      : intl.formatMessage(MSG.single, {
          term: loan?.termYears || loan?.termYearsEstimated
        })

  let status = interestStatus === 'good' ? intl.formatMessage(MSG.goodStatus) : intl.formatMessage(MSG.status)

  if (loan?.adjustable) {
    status += ` ${intl.formatMessage(MSG.armStatus)}`
  }

  return (
    <>
      <Heading as='h3' fontWeight='light'>
        {interestStatus === 'good' ? intl.formatMessage(MSG.goodHead) : intl.formatMessage(MSG.head)}
      </Heading>
      <Text align='center' size='sm'>
        {interestDiff > 0
          ? intl.formatMessage(MSG.saving, {
              loanDescription,
              status,
              interestAmount: LockableInlineCurrencyText({
                currency: interestTotal + interestDiff,
                variant: 'danger',
                locked: !withCurrentHomeData
              }),
              saveAmount: LockableInlineCurrencyText({
                currency: interestDiff,
                variant: 'success',
                locked: !withCurrentHomeData
              })
            })
          : intl.formatMessage(MSG.explainer, {
              loanDescription,
              status,
              amount: LockableInlineCurrencyText({
                currency: interestTotal,
                variant: 'danger',
                locked: !withCurrentHomeData
              })
            })}
      </Text>
    </>
  )
}
